import React from "react";
import {
  CUBE_SIDE_OUTER_BORDER_DEFAULT_COLOR,
  CUBE_SIDE_OUTER_FILL_COLOR,
  CUBE_SIDE_OUTER_BORDER_INACTIVE_COLOR,
  CUBE_SIDE_INNER_FILL_COLOR_MAX,
  CUBE_SIDE_OUTER_BORDER_ACTIVE_COLOR,
  CUBE_SIDE_INNER_ACTIVE_FILL_COLOR,
  CUBE_SIDE_INNER_INACTIVE_FILL_COLOR,
  CUBE_SIDE_INNER_FILL_COLOR_MIN,
} from "../../constants";
import { CubeSideName } from "../../types";
import {
  KpiIcon,
  StarIcon,
  StrategyIcon,
  StructureIcon,
  TeamIcon,
  VisionIcon,
} from "./sides-icons";
import { SideContainer } from "./styled";
import {
  transitionBetweenColors,
  calculateTranslateForScaledPolylineFromString,
} from "./utils";

export const CUBE_SIDE_POINTS: Record<CubeSideName, string> = {
  5: "35.7227,152.524 35.7227,57.0903 115.152,4.15225 115.152,99.5868 35.7227,152.524",
  3: "247.364,114.382 165.934,168.652 165.934,266.489 247.364,212.219 247.364,114.382",
  2: "136.117,12.8417 56.0707,66.1907 135.934,119.814 215.98,66.4654 136.117,12.8417",
  1: "217.168,192.901 135.738,138.631 54.4959,193.181 135.925,247.451 217.168,192.901",
  4: "106.572,169.578 25.1504,115.297 25.1504,213.134 106.572,267.416 106.572,169.578",
  6: "237.028,57.0575 155.9,2.33646 155.358,100.172 236.485,154.893 237.028,57.0575",
};

export const CUBE_SIDE_TO_ICON: Record<CubeSideName, React.FC> = {
  5: TeamIcon,
  3: StrategyIcon,
  2: VisionIcon,
  1: StarIcon,
  4: KpiIcon,
  6: StructureIcon,
};

export interface CubeSideProps {
  side: CubeSideName;
  onMouseEnter: (side: CubeSideName) => void;
  onMouseLeave: (side: CubeSideName) => void;
  onClick: (side: CubeSideName) => void;
  isGreyedOut: boolean;
  isActive: boolean;
  scale: number;
  fillColorSaturation?: number;
  style?: React.CSSProperties;
}

export interface ScalablePolylineProps {
  scale: number;
  points: string;
  color: string;
}

export function ScalablePolyline({
  scale,
  points,
  color,
}: ScalablePolylineProps) {
  const { translateX, translateY, scaledPointsString } =
    calculateTranslateForScaledPolylineFromString(points, scale);

  return (
    <polyline
      points={scaledPointsString}
      stroke="#22044E"
      fill={color}
      strokeWidth="2"
      transform={`translate(${translateX}, ${translateY})`}
    />
  );
}

export function CubeSide({
  side,
  onMouseEnter,
  onMouseLeave,
  onClick,
  isGreyedOut,
  isActive,
  scale,
  style,
  fillColorSaturation,
}: CubeSideProps) {
  const points = CUBE_SIDE_POINTS[side];
  let outerBorderColor = CUBE_SIDE_OUTER_BORDER_DEFAULT_COLOR;
  let innerFillColor =
    fillColorSaturation !== undefined
      ? transitionBetweenColors(
          CUBE_SIDE_INNER_FILL_COLOR_MIN,
          CUBE_SIDE_INNER_FILL_COLOR_MAX,
          fillColorSaturation
        )
      : CUBE_SIDE_INNER_FILL_COLOR_MAX;

  if (isActive) {
    outerBorderColor = CUBE_SIDE_OUTER_BORDER_ACTIVE_COLOR;
    innerFillColor = CUBE_SIDE_INNER_ACTIVE_FILL_COLOR;
  } else if (isGreyedOut) {
    innerFillColor =
      fillColorSaturation !== undefined
        ? transitionBetweenColors(
            CUBE_SIDE_INNER_FILL_COLOR_MIN,
            CUBE_SIDE_INNER_FILL_COLOR_MAX,
            fillColorSaturation
          )
        : CUBE_SIDE_INNER_INACTIVE_FILL_COLOR;
    outerBorderColor = CUBE_SIDE_OUTER_BORDER_INACTIVE_COLOR;
  }

  function handleMouseEnter() {
    onMouseEnter(side);
  }

  function handleMouseLeave() {
    onMouseLeave(side);
  }

  function handleClick() {
    onClick(side);
  }

  return (
    <SideContainer
      id={`side-${side}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      style={style}
    >
      <polyline
        points={points}
        stroke={outerBorderColor}
        fill={CUBE_SIDE_OUTER_FILL_COLOR}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <ScalablePolyline points={points} scale={scale} color={innerFillColor} />
    </SideContainer>
  );
}
